export const CHECKLIST_ITEM_TYPES = {
	HEADING: 'heading',
	TEXT: 'text',
	DROPDOWN: 'dropdown',
	CHECKBOX: 'checkbox',
	CHECKBOX_GROUP: 'checkboxGroup',
	NUMBER: 'number',
	PHOTO: 'photo',
};

const MAX_CHECKLIST_ITEMS = 80;
const MAX_ITEM_LABEL_LENGTH = 200;
const MAX_DESCRIPTION_LENGTH = 300;
const MAX_PLACEHOLDER_LENGTH = 200;
const MAX_OPTIONS_PER_ITEM = 50;
const MAX_OPTION_LABEL_LENGTH = 120;
const MAX_TEXT_ANSWER_LENGTH = 500;
const MAX_ANSWER_COMMENT_LENGTH = 500;
export const MAX_PHOTO_ANSWERS = 20;
const CONDITIONAL_SOURCE_TYPES = new Set([
	CHECKLIST_ITEM_TYPES.CHECKBOX,
	CHECKLIST_ITEM_TYPES.DROPDOWN,
	CHECKLIST_ITEM_TYPES.CHECKBOX_GROUP,
]);

const toTrimmedString = (value, maxLength = 0) => {
	if (value === undefined || value === null) {
		return '';
	}

	const trimmed = String(value).trim();
	if (!trimmed) {
		return '';
	}

	if (!maxLength || trimmed.length <= maxLength) {
		return trimmed;
	}

	return trimmed.slice(0, maxLength);
};

const buildFallbackItemId = (index) => `item_${index + 1}`;

const normalizeItemId = (value, index) => {
	const trimmed = toTrimmedString(value, 80);
	if (!trimmed) {
		return buildFallbackItemId(index);
	}

	const normalized = trimmed.replace(/[^a-zA-Z0-9_-]/g, '_');
	return normalized || buildFallbackItemId(index);
};

const normalizeChecklistItemType = (value) => {
	const normalized = toTrimmedString(value).toLowerCase();

	switch (normalized) {
		case 'section':
		case CHECKLIST_ITEM_TYPES.HEADING:
			return CHECKLIST_ITEM_TYPES.HEADING;
		case CHECKLIST_ITEM_TYPES.TEXT:
			return CHECKLIST_ITEM_TYPES.TEXT;
		case 'select':
		case CHECKLIST_ITEM_TYPES.DROPDOWN:
			return CHECKLIST_ITEM_TYPES.DROPDOWN;
		case 'boolean':
		case CHECKLIST_ITEM_TYPES.CHECKBOX:
			return CHECKLIST_ITEM_TYPES.CHECKBOX;
		case 'checkboxgroup':
		case 'checkbox_group':
		case 'multi_checkbox':
		case CHECKLIST_ITEM_TYPES.CHECKBOX_GROUP.toLowerCase():
			return CHECKLIST_ITEM_TYPES.CHECKBOX_GROUP;
		case CHECKLIST_ITEM_TYPES.NUMBER:
			return CHECKLIST_ITEM_TYPES.NUMBER;
		case CHECKLIST_ITEM_TYPES.PHOTO:
			return CHECKLIST_ITEM_TYPES.PHOTO;
		default:
			return null;
	}
};

const sanitizeChecklistItemOptions = (rawOptions) => {
	if (!Array.isArray(rawOptions)) {
		return [];
	}

	const seen = new Set();
	const sanitized = [];

	for (const option of rawOptions) {
		const optionLabel = toTrimmedString(
			typeof option === 'object' && option !== null
				? (option.label ?? option.value)
				: option,
			MAX_OPTION_LABEL_LENGTH,
		);

		if (!optionLabel) {
			continue;
		}

		const key = optionLabel.toLowerCase();
		if (seen.has(key)) {
			continue;
		}

		seen.add(key);
		sanitized.push(optionLabel);

		if (sanitized.length >= MAX_OPTIONS_PER_ITEM) {
			break;
		}
	}

	return sanitized;
};

const toOptionalNumber = (value) => {
	if (value === undefined || value === null || value === '') {
		return null;
	}

	const number = Number(value);
	return Number.isFinite(number) ? number : null;
};

export const sanitizeChecklistItems = (rawItems) => {
	if (!Array.isArray(rawItems)) {
		return [];
	}

	const seenIds = new Set();
	const sanitized = [];

	for (let index = 0; index < rawItems.length; index += 1) {
		const item = rawItems[index];
		if (!item || typeof item !== 'object') {
			continue;
		}

		const normalizedType = normalizeChecklistItemType(item.type);
		if (!normalizedType) {
			continue;
		}

		const label = toTrimmedString(item.label || item.question || item.title, MAX_ITEM_LABEL_LENGTH);
		if (!label) {
			continue;
		}

		let itemId = normalizeItemId(item.id, index);
		while (seenIds.has(itemId)) {
			itemId = `${itemId}_${index}`;
		}
		seenIds.add(itemId);

		const sanitizedItem = {
			id: itemId,
			type: normalizedType,
			label,
			required: normalizedType === CHECKLIST_ITEM_TYPES.HEADING ? false : Boolean(item.required),
		};

		const description = toTrimmedString(item.description, MAX_DESCRIPTION_LENGTH);
		if (description) {
			sanitizedItem.description = description;
		}

		const flagLabel = toTrimmedString(item.flagLabel, MAX_OPTION_LABEL_LENGTH);
		if (flagLabel) {
			sanitizedItem.flagLabel = flagLabel;
		}

		if (item.showWhen && typeof item.showWhen === 'object') {
			sanitizedItem.showWhen = {
				itemId: normalizeItemId(item.showWhen.itemId, index),
				value: item.showWhen.value,
			};
		}

		if (normalizedType === CHECKLIST_ITEM_TYPES.HEADING) {
			sanitized.push(sanitizedItem);

			if (sanitized.length >= MAX_CHECKLIST_ITEMS) {
				break;
			}

			continue;
		}

		if (normalizedType === CHECKLIST_ITEM_TYPES.TEXT) {
			const placeholder = toTrimmedString(item.placeholder, MAX_PLACEHOLDER_LENGTH);
			if (placeholder) {
				sanitizedItem.placeholder = placeholder;
			}
		}

		if ([CHECKLIST_ITEM_TYPES.DROPDOWN, CHECKLIST_ITEM_TYPES.CHECKBOX_GROUP].includes(normalizedType)) {
			const options = sanitizeChecklistItemOptions(item.options);
			if (!options.length) {
				continue;
			}

			sanitizedItem.options = options;
		}

		if (normalizedType === CHECKLIST_ITEM_TYPES.NUMBER) {
			const min = toOptionalNumber(item.min);
			const max = toOptionalNumber(item.max);
			if (min !== null) {
				sanitizedItem.min = min;
			}
			if (max !== null) {
				sanitizedItem.max = max;
			}
		}

		sanitized.push(sanitizedItem);

		if (sanitized.length >= MAX_CHECKLIST_ITEMS) {
			break;
		}
	}

	const itemsById = new Map(sanitized.map(item => [item.id, item]));
	sanitized.forEach((item, index) => {
		if (!item.showWhen) {
			return;
		}

		const sourceItem = itemsById.get(item.showWhen.itemId);
		const sourceIndex = sanitized.findIndex(candidate => candidate.id === sourceItem?.id);
		if (
			!sourceItem ||
			sourceIndex >= index ||
			!CONDITIONAL_SOURCE_TYPES.has(sourceItem.type)
		) {
			delete item.showWhen;
			return;
		}

		if (sourceItem.type === CHECKLIST_ITEM_TYPES.CHECKBOX) {
			if (typeof item.showWhen.value !== 'boolean') {
				delete item.showWhen;
			}
			return;
		}

		const allowedOptions = sanitizeChecklistItemOptions(sourceItem.options);
		const matchingOption = allowedOptions.find(option => (
			option.toLowerCase() === toTrimmedString(item.showWhen.value, MAX_OPTION_LABEL_LENGTH).toLowerCase()
		));
		if (!matchingOption) {
			delete item.showWhen;
			return;
		}

		item.showWhen.value = matchingOption;
	});

	return sanitized;
};

const normalizeOptionValue = (value) => toTrimmedString(value, MAX_OPTION_LABEL_LENGTH);

const normalizeOptionValuesArray = (value) => {
	const rawValues = Array.isArray(value)
		? value
		: (value === undefined || value === null || value === '' ? [] : [value]);
	const seen = new Set();
	const normalized = [];

	for (const item of rawValues) {
		const nextValue = normalizeOptionValue(item);
		if (!nextValue) {
			continue;
		}

		const key = nextValue.toLowerCase();
		if (seen.has(key)) {
			continue;
		}

		seen.add(key);
		normalized.push(nextValue);
	}

	return normalized;
};

const normalizeAnswerValue = (item, rawValue) => {
	if (!item || typeof item !== 'object') {
		return null;
	}

	if (item.type === CHECKLIST_ITEM_TYPES.HEADING) {
		return null;
	}

	if (item.type === CHECKLIST_ITEM_TYPES.TEXT) {
		return toTrimmedString(rawValue, MAX_TEXT_ANSWER_LENGTH);
	}

	if (item.type === CHECKLIST_ITEM_TYPES.DROPDOWN) {
		const nextValue = normalizeOptionValue(rawValue);
		const allowedOptions = new Set(sanitizeChecklistItemOptions(item.options).map(option => option.toLowerCase()));
		return allowedOptions.has(nextValue.toLowerCase()) ? nextValue : '';
	}

	if (item.type === CHECKLIST_ITEM_TYPES.CHECKBOX) {
		return Boolean(rawValue);
	}

	if (item.type === CHECKLIST_ITEM_TYPES.CHECKBOX_GROUP) {
		const allowedOptions = sanitizeChecklistItemOptions(item.options);
		const allowedSet = new Set(allowedOptions.map(option => option.toLowerCase()));

		return normalizeOptionValuesArray(rawValue)
			.filter(value => allowedSet.has(value.toLowerCase()));
	}

	if (item.type === CHECKLIST_ITEM_TYPES.NUMBER) {
		const number = Number(rawValue);
		if (!Number.isFinite(number)) {
			return null;
		}

		if (typeof item.min === 'number' && number < item.min) {
			return item.min;
		}

		if (typeof item.max === 'number' && number > item.max) {
			return item.max;
		}

		return number;
	}

	if (item.type === CHECKLIST_ITEM_TYPES.PHOTO) {
		const rawLinks = Array.isArray(rawValue)
			? rawValue
			: (rawValue === undefined || rawValue === null || rawValue === '' ? [] : [rawValue]);
		const seen = new Set();
		const links = [];

		for (const rawLink of rawLinks) {
			const link = toTrimmedString(rawLink, MAX_TEXT_ANSWER_LENGTH);
			// Only uploaded media links are valid; local device URIs (file://) from
			// interrupted offline uploads must never be persisted.
			if (!link || !(link.startsWith('http://') || link.startsWith('https://'))) {
				continue;
			}

			const key = link.toLowerCase();
			if (seen.has(key)) {
				continue;
			}

			seen.add(key);
			links.push(link);

			if (links.length >= MAX_PHOTO_ANSWERS) {
				break;
			}
		}

		return links;
	}

	return null;
};

const answersToMap = (answers) => {
	if (Array.isArray(answers)) {
		return new Map(answers.map(answer => [answer?.itemId, answer?.value]));
	}

	return new Map(Object.entries(answers || {}));
};

const answerMatchesCondition = (sourceItem, answerValue, conditionValue) => {
	if (sourceItem.type === CHECKLIST_ITEM_TYPES.CHECKBOX) {
		return answerValue === conditionValue;
	}

	if (sourceItem.type === CHECKLIST_ITEM_TYPES.CHECKBOX_GROUP) {
		return Array.isArray(answerValue) && answerValue.includes(conditionValue);
	}

	return answerValue === conditionValue;
};

export const getVisibleChecklistItems = (items, answers) => {
	if (!Array.isArray(items)) {
		return [];
	}

	const answerValues = answersToMap(answers);
	const itemsById = new Map(items.map(item => [item?.id, item]));
	const visibleItemIds = new Set();

	return items.filter((item) => {
		if (!item?.showWhen) {
			visibleItemIds.add(item?.id);
			return true;
		}

		const sourceItem = itemsById.get(item.showWhen.itemId);
		const isVisible = Boolean(sourceItem &&
			visibleItemIds.has(sourceItem.id) &&
			answerMatchesCondition(
				sourceItem,
				answerValues.get(sourceItem.id),
				item.showWhen.value,
			));
		if (isVisible) {
			visibleItemIds.add(item.id);
		}
		return isVisible;
	});
};

export const sanitizeChecklistAnswers = (items, rawAnswers) => {
	if (!Array.isArray(items) || !Array.isArray(rawAnswers)) {
		return [];
	}

	const itemsById = items.reduce((acc, item) => {
		if (item?.id && item.type !== CHECKLIST_ITEM_TYPES.HEADING) {
			acc.set(item.id, item);
		}
		return acc;
	}, new Map());
	const sanitized = [];

	for (const rawAnswer of rawAnswers) {
		if (!rawAnswer || typeof rawAnswer !== 'object') {
			continue;
		}

		const itemId = toTrimmedString(rawAnswer.itemId, 80);
		const item = itemsById.get(itemId);
		if (!item) {
			continue;
		}

		const sanitizedAnswer = {
			itemId,
			value: normalizeAnswerValue(item, rawAnswer.value),
		};
		const comment = toTrimmedString(rawAnswer.comment, MAX_ANSWER_COMMENT_LENGTH);
		if (comment) {
			sanitizedAnswer.comment = comment;
		}

		sanitized.push(sanitizedAnswer);
	}

	const visibleItemIds = new Set(getVisibleChecklistItems(items, sanitized).map(item => item.id));
	return sanitized.filter(answer => visibleItemIds.has(answer.itemId));
};

export const getFlaggedItemIds = (items, answers) => {
	if (!Array.isArray(items) || !Array.isArray(answers)) {
		return [];
	}

	const visibleItemIds = new Set(getVisibleChecklistItems(items, answers).map(item => item.id));
	const itemsById = items.reduce((acc, item) => {
		if (item?.id && item.flagLabel && visibleItemIds.has(item.id)) {
			acc.set(item.id, item);
		}
		return acc;
	}, new Map());
	const flagged = [];

	for (const answer of answers) {
		const item = itemsById.get(answer?.itemId);
		if (!item) {
			continue;
		}

		const flagLabel = toTrimmedString(item.flagLabel, MAX_OPTION_LABEL_LENGTH).toLowerCase();
		if (!flagLabel) {
			continue;
		}

		if (item.type === CHECKLIST_ITEM_TYPES.CHECKBOX && answer.value === true) {
			flagged.push(item.id);
			continue;
		}

		if (item.type === CHECKLIST_ITEM_TYPES.DROPDOWN) {
			const value = toTrimmedString(answer.value, MAX_OPTION_LABEL_LENGTH).toLowerCase();
			if (value && value === flagLabel) {
				flagged.push(item.id);
			}
			continue;
		}

		if (item.type === CHECKLIST_ITEM_TYPES.CHECKBOX_GROUP) {
			const values = Array.isArray(answer.value) ? answer.value : [];
			if (values.some(value => toTrimmedString(value, MAX_OPTION_LABEL_LENGTH).toLowerCase() === flagLabel)) {
				flagged.push(item.id);
			}
		}
	}

	return flagged;
};

// Read-only text for a submitted answer (photos are shown as images instead).
export const formatChecklistAnswerValue = (item, answer) => {
	if (!answer || answer.value === undefined || answer.value === null || answer.value === '') {
		return '—';
	}

	switch (item.type) {
		case CHECKLIST_ITEM_TYPES.CHECKBOX:
			return answer.value === true ? 'Yes' : 'No';
		case CHECKLIST_ITEM_TYPES.CHECKBOX_GROUP:
			return Array.isArray(answer.value) && answer.value.length ? answer.value.join(', ') : '—';
		default:
			return String(answer.value);
	}
};

// Whether two answer lists say the same thing. Photo answers match on their number of
// photos only: a retried offline submission uploads its photos again under new links.
export const haveSameChecklistAnswers = (items, answers, otherAnswers) => {
	const photoItemIds = new Set((items || [])
		.filter(item => item?.type === CHECKLIST_ITEM_TYPES.PHOTO)
		.map(item => item.id));
	const comparable = list => JSON.stringify((list || []).map(answer => (
		photoItemIds.has(answer?.itemId) && Array.isArray(answer.value)
			? { ...answer, value: answer.value.length }
			: answer
	)));

	return comparable(answers) === comparable(otherAnswers);
};
